import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Search, Calendar, Tag } from 'lucide-react';
import { posts, allTags, formatDate } from '../data/blogIndex';

// Read time from excerpt length is meaningless; the listing shows the topic
// tags instead. Full read time is computed on the post page from the article.

// Editorial groupings for the default (unfiltered) browsing view, derived from
// each post's existing tags. Gives the index page real curated structure and
// heading text instead of a single flat card grid, and keeps every post in
// exactly one group. Update this list when a post's primary topic changes.
const topicGroups = [
  {
    category: 'Getting started',
    desc: 'New to VPNs? Start here for the fundamentals and setup walkthroughs.',
    slugs: ['what-is-vpn-complete-guide', 'vpn-setup-guide-beginners', 'vpn-router-setup-guide'],
  },
  {
    category: 'Business & compliance',
    desc: 'How a VPN fits into remote work, HIPAA safeguards, and buying decisions for teams.',
    slugs: [
      'vpn-for-remote-work-security',
      'business-vpn-benefits',
      'how-to-choose-business-vpn',
      'hipaa-vpn-requirements-healthcare',
      'remote-work-security-best-practices',
    ],
  },
  {
    category: 'Privacy & security',
    desc: 'The protocols, threats, and habits that keep your connection private.',
    slugs: ['protect-privacy-online-2025', 'public-wifi-security-risks', 'wireguard-vs-openvpn-comparison'],
  },
  {
    category: 'Gaming & streaming',
    desc: 'Lower ping, hidden IPs, and unblocking your favorite platforms.',
    slugs: ['vpn-gaming-benefits', 'vpn-streaming-unblock-content'],
  },
];

function Card({ post, featured = false }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group flex flex-col overflow-hidden rounded-xl border border-white/[0.08] bg-dark-800/40 hover:border-brand-500/30 transition-colors ${
        featured ? 'lg:flex-row' : ''
      }`}
    >
      <div className={`relative overflow-hidden bg-dark-800 ${featured ? 'lg:w-1/2' : ''}`}>
        <img
          src={post.image}
          alt={post.title}
          loading={featured ? 'eager' : 'lazy'}
          width="1200"
          height="675"
          className={`w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] ${
            featured ? 'h-56 lg:h-full' : 'h-48'
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-900/40 to-transparent" />
      </div>

      <div className={`flex flex-col p-6 ${featured ? 'lg:w-1/2 lg:p-8 lg:justify-center' : ''}`}>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-mono uppercase tracking-[0.16em] text-brand-400">
            {post.tags?.[0]}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(post.date)}
          </span>
        </div>

        <h3
          className={`font-display font-bold text-white leading-snug mb-3 group-hover:text-brand-300 transition-colors ${
            featured ? 'text-2xl md:text-3xl' : 'text-lg'
          }`}
        >
          {post.title}
        </h3>

        <p className={`text-gray-400 leading-relaxed ${featured ? 'text-base mb-6' : 'text-sm mb-4 line-clamp-3'}`}>
          {post.excerpt}
        </p>

        <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-brand-400 group-hover:gap-2.5 transition-all">
          Read article <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState('All');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      const matchesTag = activeTag === 'All' || (p.tags || []).includes(activeTag);
      const matchesQuery =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(q));
      return matchesTag && matchesQuery;
    });
  }, [query, activeTag]);

  // Featured only when showing the unfiltered, unsearched full list.
  const showFeatured = activeTag === 'All' && query.trim() === '';
  const featured = showFeatured ? filtered[0] : null;
  const rest = showFeatured ? filtered.slice(1) : filtered;

  return (
    <>
      <Helmet>
        <title>VPN Security & Privacy Blog | SACVPN</title>
        <meta
          name="description"
          content="Guides and tutorials on VPN technology, online privacy, and cybersecurity from the SACVPN team. WireGuard, remote work, gaming, streaming, and more."
        />
        <meta name="keywords" content="VPN blog, WireGuard, online privacy, cybersecurity, VPN guides, VPN tutorials" />
        <link rel="canonical" href="https://www.sacvpn.com/blog" />
        <meta property="og:title" content="VPN Security & Privacy Blog | SACVPN" />
        <meta
          property="og:description"
          content="Guides and tutorials on VPN technology, online privacy, and cybersecurity from the SACVPN team."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.sacvpn.com/blog" />
        <meta property="og:image" content="https://www.sacvpn.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="bg-dark-900">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-white/5">
          <div className="absolute inset-0 bg-[radial-gradient(800px_400px_at_75%_10%,rgba(14,165,233,0.12),transparent_60%)]" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-14">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">SACVPN Blog</p>
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white tracking-tight max-w-3xl leading-[1.08]">
              VPN, privacy, and security, explained clearly
            </h1>
            <p className="mt-5 text-lg text-gray-400 max-w-2xl leading-relaxed">
              Practical guides on VPN technology, online privacy, and cybersecurity, written for real people and real teams.
              {' '}
              {posts.length} articles across setup guides, privacy fundamentals, business and HIPAA compliance, and gaming and streaming.
            </p>

            {/* Search + tag filters */}
            <div className="mt-9 flex flex-col gap-5">
              <div className="relative max-w-md">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search articles"
                  aria-label="Search articles"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-brand-500 transition-colors"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                {['All', ...allTags].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setActiveTag(tag)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      activeTag === tag
                        ? 'bg-brand-500 text-white'
                        : 'bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    {tag !== 'All' && <Tag className="w-3 h-3" />}
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Posts */}
        <section className="max-w-7xl mx-auto px-6 py-16">
          {filtered.length === 0 ? (
            <p className="text-gray-400 text-center py-16">
              No articles match your search. Try a different term or tag.
            </p>
          ) : (
            <>
              {featured && (
                <div className="mb-12">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-gray-500 mb-5">Latest</p>
                  <Card post={featured} featured />
                </div>
              )}

              {rest.length > 0 && (
                showFeatured ? (
                  <div className="space-y-14">
                    {topicGroups.map((group) => {
                      const groupPosts = rest.filter((p) => group.slugs.includes(p.slug));
                      if (groupPosts.length === 0) return null;
                      return (
                        <div key={group.category}>
                          <p className="text-xs font-mono uppercase tracking-[0.2em] text-gray-500 mb-1.5">
                            {group.category}
                          </p>
                          <p className="text-sm text-gray-500 mb-5 max-w-2xl">{group.desc}</p>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {groupPosts.map((post) => (
                              <Card key={post.slug} post={post} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rest.map((post) => (
                      <Card key={post.slug} post={post} />
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </section>

        {/* CTA */}
        <section className="border-t border-white/5">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center">
            <h2 className="text-2xl md:text-3xl font-display font-bold text-white tracking-tight mb-4">
              Put your traffic on a private network
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto mb-8">
              SACVPN routes your connection through a global WireGuard network. Start a 14-day free trial, no credit card required.
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
            >
              View pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
